// C:\Projects\texe-web\src\pages\Login.tsx
import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { humanizeApiError } from "../lib/api";
import { useAuth } from "../context/AuthProvider";

type LoginResponse =
  | {
      ok: true;
      access?: string;
      token?: string;
      accessToken?: string;
      user?: { id: string; username: string; email: string };
    }
  | any;

export default function Login() {
  const nav = useNavigate();
  const { login: loginWithContext } = useAuth();

  const [form, setForm] = useState({ emailOrUsername: "", password: "" });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    
    // Client-side validation
    if (!form.emailOrUsername || !form.password) {
      setError('Her düşüncenin bir kimliği vardır.');
      return;
    }

    if (form.password.length < 6) {
      setError('Yanlış bir giriş, doğru bir düşünceye engel değildir.');
      return;
    }

    if (form.emailOrUsername.length < 3) {
      setError('Lütfen bilgilerinizi kontrol edin.');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      await loginWithContext({
        emailOrUsername: form.emailOrUsername,
        password: form.password,
      });

      nav("/feed", { replace: true });
    } catch (err: any) {
      // Özel hata mesajları
      if (err.code === 'invalid_credentials' || err.message?.includes('invalid_credentials')) {
        setError('Yanlış bir giriş, doğru bir düşünceye engel değildir.');
      } else if (err.code === 'session_expired' || err.message?.includes('Session expired')) {
        setError('Yanlış bir giriş, doğru bir düşünceye engel değildir.');
      } else if (err.code === 'bad_input' || err.message?.includes('bad_input')) {
        setError('Yanlış bir giriş, doğru bir düşünceye engel değildir.');
      } else {
        const raw = err?.message && err.message.length < 200 ? err.message : "api_error";
        setError(humanizeApiError(raw));
      }
    } finally {
      setLoading(false);
    }
  }

  const canSubmit = form.emailOrUsername.trim().length >= 3 && form.password.length >= 6;

  return (
    <div className="login-container">
      <div className="login-content">
        {/* Logo */}
        <div className="logo-container">
          <img src="/logo/logo.png" alt="SavNot" className="logo-image" />
          <h1 className="app-name">SavNot</h1>
        </div>

        <h2 className="login-title">Giriş Yap</h2>

        <form onSubmit={onSubmit} className="login-form">
          {/* Email or Username */}
          <input
            className="login-input"
            type="text"
            value={form.emailOrUsername}
            onChange={(e) => setForm({ ...form, emailOrUsername: e.target.value })}
            placeholder="E-posta veya Kullanıcı Adı"
            autoComplete="username email"
            autoCapitalize="none"
          />

          {/* Password */}
          <input
            className="login-input"
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder="Şifre"
            autoComplete="current-password"
          />

          {/* Error */}
          {error && <p className="login-error">{error}</p>}

          {/* Login Button */}
          <button
            type="submit"
            className="login-button"
            disabled={loading || !canSubmit}
          >
            {loading ? 'Yükleniyor...' : 'Giriş Yap'}
          </button>

          {/* Forgot Password Link */}
          <Link to="/forgot" className="forgot-link">
            Şifremi Unuttum?
          </Link>

          {/* Register Link */}
          <p className="register-link">
            Hesabın yok mu? <Link to="/register" className="register-link-bold">Kayıt ol</Link>
          </p>
          <div className="login-footer">
            <div className="footer-links">
              <Link to="/terms" className="footer-link">📜 Kullanıcı Sözleşmesi</Link>
            </div>
          </div>
        </form>
      </div>

      <style>{`
        .login-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #000;
          padding: 24px;
        }

        .login-content {
          width: 100%;
          max-width: 420px;
        }

        .logo-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          margin-bottom: 48px;
        }

        .logo-image {
          width: 80px;
          height: 80px;
          object-fit: contain;
        }

        .app-name {
          font-size: 24px;
          font-weight: 800;
          color: #114232;
          margin-top: 8px;
          margin-bottom: 0;
        }

        .login-title {
          font-size: 28px;
          font-weight: 800;
          color: #fff;
          margin-bottom: 24px;
          text-align: center;
        }

        .login-form {
          display: flex;
          flex-direction: column;
        }

        .login-input {
          background-color: #1a1a1a;
          border: 2px solid #114232;
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 16px;
          font-size: 16px;
          color: #fff;
          width: 100%;
          box-sizing: border-box;
        }

        .login-input::placeholder {
          color: #666;
        }

        .login-input:focus {
          outline: none;
          border-color: #1a6b4f;
        }

        .login-button {
          background-color: #114232;
          border: none;
          border-radius: 12px;
          padding: 16px;
          color: #fff;
          font-size: 16px;
          font-weight: 700;
          cursor: pointer;
          margin-top: 8px;
          width: 100%;
        }

        .login-button:hover:not(:disabled) {
          background-color: #1a6b4f;
        }

        .login-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .login-error {
          color: #ff6b6b;
          text-align: center;
          margin-bottom: 12px;
          font-size: 14px;
        }

        .forgot-link {
          color: #114232;
          font-size: 14px;
          font-weight: 600;
          text-align: center;
          margin-top: 16px;
          text-decoration: none;
          display: block;
        }

        .forgot-link:hover {
          text-decoration: underline;
        }

        .register-link {
          color: #ccc;
          font-size: 14px;
          text-align: center;
          margin-top: 24px;
        }

        .register-link-bold {
          color: #114232;
          font-weight: 700;
          text-decoration: none;
        }

        .register-link-bold:hover {
          text-decoration: underline;
        }

        .login-footer {
          margin-top: 32px;
          padding-top: 24px;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          text-align: center;
        }

        .footer-links {
          display: flex;
          flex-direction: column;
          gap: 12px;
          align-items: center;
        }

        .footer-link {
          color: #888;
          font-size: 13px;
          text-decoration: none;
          transition: color 0.3s;
        }

        .footer-link:hover {
          color: #114232;
        }
      `}</style>
    </div>
  );
}

